import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { collection, doc, updateDoc, addDoc, getDocs, query, where, orderBy, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import LineItem2 from './LineItem2';
import { ORDER_STATUSES, LINE_STATUSES, LINE_TYPES } from '../firebase/schema';
import { normalizeFilesForFirestore, getMockupUrl, getPersistableMockupUrl } from '../utils/fileHelpers';
import { registerDesignMockupFromLine } from '../utils/designMockupIndex';
import {
  maybeSetOrderReadyToInvoiceIfAllLinesComplete,
  designOnlyLineCompletionFields,
  isDesignOnlyLineType
} from '../utils/orderStatusSync';
import { sampleLineProductionSkipFields } from '../utils/sampleOrder';
import { spPrintBlockReasonForCustomerLines } from '../utils/spLicenseStore';
import { EMPLOYEE_ROLES } from '../constants/employeeRoles';
import { computeSalesTax, sumTaxableFeesAmount, resolveTaxExempt, resolveOrderTaxExemptFlag } from '../utils/orderSalesTax';
import { computeUpchargeQuantity } from '../utils/orderTotals';
import { activityLogAppend, activityLogInit } from '../utils/lineActivityLog';
import { duplicateLineItem } from '../utils/duplicateLineItem';
import { moveLineInArray } from '../utils/reorderLineItems';
import { FACILITIES, resolveFacilityId, resolveHomeFacilityId } from '../utils/facilities';
import SuccessAnimation from './SuccessAnimation';
import Modal from './Modal';
import FeesModal from './FeesModal';

/**
 * Safely format a date value for HTML date input
 * Handles: Firestore Timestamp, Date object, ISO string, or null/undefined
 */
const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    // Firestore Timestamp (has toDate method)
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toISOString().split('T')[0];
    }
    
    // JavaScript Date object
    if (dateValue instanceof Date) {
      return dateValue.toISOString().split('T')[0];
    }
    
    // Already a string - might be ISO format or just date
    if (typeof dateValue === 'string') {
      // If it contains 'T', split it; otherwise return as-is (might be YYYY-MM-DD already)
      return dateValue.includes('T') ? dateValue.split('T')[0] : dateValue;
    }
    
    // Number (Unix timestamp in milliseconds)
    if (typeof dateValue === 'number') {
      return new Date(dateValue).toISOString().split('T')[0];
    }
    
    // Firestore Timestamp might also have seconds/nanoseconds
    if (dateValue?.seconds) {
      return new Date(dateValue.seconds * 1000).toISOString().split('T')[0];
    }
    
    return '';
  } catch (error) {
    console.warn('Error formatting date:', dateValue, error);
    return '';
  }
};


// Styled Components
const PageContainer = styled.div`
  padding: 20px;
  background: var(--al-bg);
  min-height: 100%;
  color: white;
  overflow: auto;
  position: relative;
`;

const HeaderSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const LinesSection = styled.div`
  margin-bottom: 24px;
`;

const FooterSection = styled.div`
  display: flex;
  gap: 24px;
  align-items: stretch;
`;

const CustomerInfo = styled.div`
  font-size: 1.2em;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const CustomerSearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const CustomerSearchInput = styled.input`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  padding: 8px;
  width: 100%;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.9em;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--al-surface);
  border: 1px solid ${props => props.theme.colors.border};
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 2px;
`;

const SearchResultItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.85em;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: black;
  }
`;

const ProjectManagerSelect = styled.select`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  padding: 8px;
  width: 100%;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.9em;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  option {
    background: var(--al-surface);
    color: white;
  }
`;

const DateContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const DateInput = styled.input`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  padding: 8px;
  width: 100%;
`;

const UpdateButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: black;
  border: none;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddLineButton = styled.button`
  background: transparent;
  border: 1px dashed ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  padding: 12px;
  width: 100%;
  cursor: pointer;
  margin-top: 16px;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const NotesBox = styled.textarea`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  padding: 12px;
  flex: 2;
  min-width: 200px;
  resize: none;
`;

const FeeGrid = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  width: 100%;
`;

const FeeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 120px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const UpchargeColumn = styled(FeeColumn)`
  background: rgba(255, 255, 255, 0.02);
`;

const FeesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 120px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  .header {
    padding: 4px;
    font-weight: 600;
    font-size: 0.85em;
  }
  
  .summary {
    font-size: 0.8em;
    color: rgba(255, 255, 255, 0.6);
    flex: 1;
  }
  
  .fee-line {
    display: flex;
    justify-content: space-between;
    padding: 2px 0;
  }
  
  .fee-type {
    color: rgba(255, 255, 255, 0.8);
  }
  
  .fee-amount {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
  }
  
  .instructions {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .instruction-tag {
    background: ${props => props.theme.colors.primary};
    color: #000;
    padding: 2px 6px;
    font-size: 0.7em;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const EditFeesButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  padding: 8px 16px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.75em;
  margin-top: auto;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const FeeHeader = styled.div`
  select {
    background: transparent;
    border: 1px solid ${props => props.theme.colors.border};
    color: ${props => props.theme.colors.text};
    padding: 4px;
    width: 100%;
  }
`;

const FeeRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  
  label {
    min-width: 45px;
    width: 45px;
    text-align: right;
    font-size: 0.85em;
    flex-shrink: 0;
  }
  
  input {
    background: transparent;
    border: 1px solid ${props => props.theme.colors.border};
    color: ${props => props.theme.colors.text};
    padding: 6px 8px;
    flex: 1;
    min-width: 80px;
    font-family: 'Montserrat', sans-serif;
  }
`;

const TotalsDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 120px;
  padding: 12px;
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  .header {
    padding: 4px;
    font-weight: 700;
    font-size: 0.9em;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  label {
    color: white !important;
    font-weight: 600;
  }
  
  input {
    background: transparent !important;
    border: none !important;
    color: ${props => props.theme.colors.primary} !important;
    font-weight: 700 !important;
    font-size: 1em !important;
  }
`;

const TotalPieces = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const TotalAmount = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const CogsPercent = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ConfirmDialogContent = styled.div`
  background: var(--al-surface);
  border: 1px solid ${props => props.theme.colors.border};
  padding: 24px;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ConfirmDialogTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  margin: 0 0 16px 0;
  font-size: 1.1em;
  text-transform: uppercase;
`;

const ConfirmDialogText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 24px 0;
  font-size: 0.9em;
`;

const ConfirmDialogButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  padding: 10px 24px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.85em;
  border: none;
  
  ${props => props.variant === 'danger' ? `
    background: #FF4C4C;
    color: white;
    
    &:hover {
      background: #FF3333;
    }
  ` : `
    background: transparent;
    color: ${props.theme.colors.text};
    border: 1px solid ${props.theme.colors.border};
    
    &:hover {
      border-color: ${props.theme.colors.primary};
      color: ${props.theme.colors.primary};
    }
  `}
`;

// Helper function
const generateUniqueId = (prefix = '') => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}-${Date.now()}`;
};

// Helper to deduplicate project managers by name
const deduplicatePMs = (pms) => {
  if (!Array.isArray(pms)) return [];
  
  const seen = new Set();
  return pms.filter(pm => {
    const value = typeof pm === 'object' ? pm.name : pm;
    const duplicate = seen.has(value);
    seen.add(value);
    return !duplicate;
  });
};

// Helper function to calculate 2XL+ upcharge quantities
const calculate2XLPlus = (lines) => {
  return lines.reduce((total, line) => total + computeUpchargeQuantity(line), 0);
};

// Helper function to check if any instructions are active
const hasActiveInstructions = (instructions) => {
  if (!instructions) return false;
  return instructions.shipping || instructions.sorting || instructions.folding ||
         instructions.bagging || instructions.fulfillment || instructions.neckTag ||
         instructions.taxExempt || instructions.designWorkOnly || instructions.doNotBill ||
         instructions.followUpEmail || instructions.instagramWorthy || instructions.providedGoods ||
         instructions.sample ||
         !!instructions.other;
};

// Format minutes as H:MM
const formatMinutes = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

// Calculate print time for a line (setup + print)
const calculatePrintMinutes = (line) => {
  const quantity = Object.values(line.sizes || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
  if (quantity <= 0) return 0;
  
  // Count ink colors
  const inkCount = (line.frontColors?.length || 0) +
                   (line.backColors?.length || 0) +
                   (line.sleeveColors?.length || 0) +
                   (line.otherInkColors?.length || 0);
  
  // Base setup time (15 minutes per color)
  const setupTime = inkCount * 15;
  
  // Print time (assume 240 pieces per hour = 4 pieces per minute)
  const printTime = quantity / 4;
  
  return setupTime + printTime;
};

// Calculate print revenue for a line (revenue after COGS)
const calculatePrintRevenue = (line) => {
  const quantity = Object.values(line.sizes || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
  const price = Number(line.pricePerPiece) || 0;
  const cogs = Number(line.cogs) || 0;
  
  if (quantity <= 0 || price <= 0) return 0;
  
  const revenuePerPiece = Math.max(0, price - cogs);
  return quantity * revenuePerPiece;
};

// Calculate press rate ($/hour)
const calculatePressRate = (revenue, minutes) => {
  if (!revenue || !minutes || minutes <= 0) return 0;
  const hours = minutes / 60;
  return revenue / hours;
};

// Component
const EditOrder = ({ order, isOpen, onClose }) => {
  const [orderData, setOrderData] = useState(order || {});
  const [fees, setFees] = useState([]);
  const [shippingDetails, setShippingDetails] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalLines, setOriginalLines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projectManagers, setProjectManagers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [deletedLineIds, setDeletedLineIds] = useState([]);
  const [lineToDelete, setLineToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFeesModal, setShowFeesModal] = useState(false);
  const [customerTaxExempt, setCustomerTaxExempt] = useState(false);

  // Fetch customers and project managers
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch customers
        const customersQuery = query(collection(db, 'customers'), orderBy('client'));
        const customersSnapshot = await getDocs(customersQuery);
        const customersData = customersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCustomers(customersData);

        // Fetch project managers
        const pmsQuery = query(
          collection(db, 'employees'),
          where('role', '==', EMPLOYEE_ROLES.PROJECT_MANAGER)
        );
        const pmsSnapshot = await getDocs(pmsQuery);
        const pmsData = pmsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(pm => pm.active)
          .sort((a, b) => a.name.localeCompare(b.name));
        setProjectManagers(pmsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Update orderData when order prop changes
  useEffect(() => {
    if (order) {
      const normalizedFees = Array.isArray(order.fees) && order.fees.length > 0
        ? order.fees
        : [
            { id: 1, type: order.fee1Type || '', quantity: order.fee1Quantity || '', price: order.fee1Price || '' },
            { id: 2, type: order.fee2Type || '', quantity: order.fee2Quantity || '', price: order.fee2Price || '' }
          ];
      setOrderData({
        ...order,
        facilityId: resolveFacilityId(order.facilityId),
        // Stays put when production moves, so customer emails keep the right brand
        homeFacilityId: resolveHomeFacilityId(order),
        upchargePrice: order.upchargePrice ?? '2.00'
      });
      setFees(normalizedFees);
      setShippingDetails(order.shippingDetails || null);
    }
  }, [order]);

  // Set initial customer search value
  useEffect(() => {
    if (orderData.customerName) {
      setCustomerSearch(orderData.customerName);
    }
  }, [orderData.customerName]);

  useEffect(() => {
    if (!orderData.customerId) {
      setCustomerTaxExempt(false);
      return;
    }
    const match = customers.find((c) => c.id === orderData.customerId);
    setCustomerTaxExempt(Boolean(match?.taxExempt));
  }, [orderData.customerId, customers]);

  // Fetch existing lines - only run when order.id changes
  useEffect(() => {
    const fetchOrderLines = async () => {
      if (!order?.id) {
        setLoading(false);
        return;
      }
      
      try {
        const linesQuery = query(
          collection(db, 'orderLines'),
          where('orderId', '==', order.id),
          orderBy('lineNumber', 'asc')
        );
        
        const linesSnapshot = await getDocs(linesQuery);
        const existingLines = linesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            docId: doc.id,
            design: data.designName || '',    // Note: changed from design to designName
            style: data.garmentId || '',      // Note: changed from style to garmentId
            color: data.garmentColor || '',   // Note: changed from color to garmentColor
            price: data.pricePerPiece || 0,
            inkColors: data.inkColors || [],
            sizes: data.sizes || {},
            ...data,
            modified: false  // Initialize as not modified
          };
        });
        
        setLines(existingLines);
        setOriginalLines(existingLines);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching order lines:', error);
        setLoading(false);
      }
    };

    fetchOrderLines();
  }, [order?.id]); // Only depend on order.id

  // Handle customer search
  const handleCustomerSearch = (e) => {
    const query = e.target.value;
    setCustomerSearch(query);
    if (query.trim() === '') {
      setFilteredCustomers([]);
      return;
    }
    const filtered = customers.filter(customer =>
      customer.client.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCustomers(filtered);
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setOrderData(prev => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.client
    }));
    setCustomerTaxExempt(customer.taxExempt === true);
    setCustomerSearch(customer.client);
    setFilteredCustomers([]);
  };

  // Handle line update with memoized callback. Explicitly preserve artwork fields when
  // the update doesn't include them so we never drop mockup/designFileUrl from any code path.
  const handleLineUpdate = useCallback((updatedLine, index) => {
    setLines(prevLines => {
      const newLines = [...prevLines];
      const prev = newLines[index];
      newLines[index] = {
        ...prev,
        ...updatedLine,
        // Map LineItem2 field names back to database field names
        designName: updatedLine.design || prev.designName,
        garmentId: updatedLine.style || prev.garmentId,
        garmentColor: updatedLine.color || prev.garmentColor,
        pricePerPiece: updatedLine.price ?? prev.pricePerPiece,
        // Preserve artwork/mockup fields unless this update explicitly sets them (prevents mockup loss)
        files: updatedLine.files !== undefined ? updatedLine.files : prev.files,
        designFileUrl: updatedLine.designFileUrl !== undefined ? updatedLine.designFileUrl : prev.designFileUrl,
        artworkUrl: updatedLine.artworkUrl !== undefined ? updatedLine.artworkUrl : prev.artworkUrl,
        mockupUrl: updatedLine.mockupUrl !== undefined ? updatedLine.mockupUrl : prev.mockupUrl,
        modified: true
      };
      return newLines;
    });
  }, []);

  // Handle line delete request (shows confirmation)
  const handleDeleteRequest = (line) => {
    setLineToDelete(line);
    setShowDeleteConfirm(true);
  };

  // Handle confirmed delete
  const handleConfirmDelete = () => {
    if (lineToDelete) {
      // If it's an existing line (has docId), track it for deletion from Firestore
      if (lineToDelete.docId) {
        setDeletedLineIds(prev => [...prev, lineToDelete.docId]);
      }
      // Remove from local state
      setLines(prevLines => prevLines.filter(l => l.id !== lineToDelete.id));
    }
    setShowDeleteConfirm(false);
    setLineToDelete(null);
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setLineToDelete(null);
  };

  // Handle submit with changes only
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Update order data - explicitly include customer and PM fields
      const orderRef = doc(db, 'orders', order.id);
      const normalizedFees = (Array.isArray(fees) ? fees : [])
        .filter((fee) => fee?.type)
        .map((fee, index) => ({
          id: fee.id || `fee-${index + 1}`,
          type: String(fee.type),
          quantity: Number(fee.quantity) || 0,
          price: Number(fee.price) || 0,
          taxable: fee.taxable !== false
        }));
      const [fee1 = {}, fee2 = {}] = normalizedFees;
      const prevFacilityId = resolveFacilityId(order?.facilityId);
      const nextFacilityId = resolveFacilityId(orderData.facilityId);
      const homeFacilityId = resolveFacilityId(orderData.homeFacilityId);
      if (prevFacilityId !== nextFacilityId) {
        const label = (id) => FACILITIES.find((f) => f.id === id)?.label || id;
        const homeNote = homeFacilityId !== nextFacilityId
          ? `\n\nCustomer emails and pickup stay with ${label(homeFacilityId)}. If this order was entered at the wrong facility, change Home Facility too.`
          : '';
        const ok = window.confirm(
          `Move production to ${label(nextFacilityId)}? It will leave the other facility's queues.${homeNote}`
        );
        if (!ok) {
          setIsSubmitting(false);
          return;
        }
      }
      const orderUpdateData = {
        ...orderData,
        customerName: orderData.customerName,
        customerId: orderData.customerId,
        taxExempt: resolveOrderTaxExemptFlag({
          order: orderData,
          customer: customerTaxExempt ? { taxExempt: true } : null,
        }),
        projectManager: orderData.projectManager,
        facilityId: nextFacilityId,
        homeFacilityId,
        dueDate: orderData.dueDate,
        notes: orderData.notes,
        fee1Type: fee1.type || '',
        fee1Quantity: fee1.quantity || 0,
        fee1Price: fee1.price || 0,
        fee2Type: fee2.type || '',
        fee2Quantity: fee2.quantity || 0,
        fee2Price: fee2.price || 0,
        fees: normalizedFees,
        upchargePrice: (orderData.upchargePrice === '' || orderData.upchargePrice == null || Number.isNaN(parseFloat(orderData.upchargePrice))) ? 2 : parseFloat(orderData.upchargePrice),
        discount: orderData.discount || null,
        additionalInstructions: orderData.additionalInstructions || null,
        ...(shippingDetails && { shippingDetails }),
        updatedAt: serverTimestamp()
      };
      
      // Firestore does not accept undefined; remove any undefined values so we don't send them.
      // Omitting a field leaves the existing document value unchanged.
      Object.keys(orderUpdateData).forEach(key => {
        if (orderUpdateData[key] === undefined) {
          delete orderUpdateData[key];
        }
      });
      
      console.log('Updating order with data:', {
        customerName: orderUpdateData.customerName,
        customerId: orderUpdateData.customerId,
        projectManager: orderUpdateData.projectManager,
        orderId: order.id
      });
      
      try {
        await updateDoc(orderRef, orderUpdateData);
        console.log('✅ Order document updated successfully');
      } catch (updateError) {
        console.error('❌ Error updating order document:', updateError);
        throw updateError;
      }

      // When facility changes, denormalize onto all lines so queues stay in sync
      if (prevFacilityId !== nextFacilityId) {
        const linesSnap = await getDocs(
          query(collection(db, 'orderLines'), where('orderId', '==', order.id))
        );
        await Promise.all(
          linesSnap.docs.map((lineDoc) =>
            updateDoc(lineDoc.ref, {
              facilityId: nextFacilityId,
              updatedAt: serverTimestamp()
            })
          )
        );
      }

      // Helper function to convert LINE_TYPES back to database format
      const convertTypeToDb = (lineType) => {
        if (!lineType) return 'screen';
        const typeStr = String(lineType).toLowerCase();
        // Map LINE_TYPES values back to database format
        if (typeStr.includes('screen')) return 'screen';
        if (typeStr.includes('embroidery')) return 'embroidery';
        if (typeStr === 'dtg') return 'dtg';
        if (typeStr === 'dtf') return 'dtf';
        if (typeStr.includes('design')) return 'design_only';
        return 'screen'; // default
      };

      // Helper function to recursively remove File objects and undefined/null values
      const cleanData = (obj) => {
        if (obj === null || obj === undefined) {
          return undefined;
        }
        if (obj instanceof File) {
          return undefined; // Remove File objects
        }
        if (Array.isArray(obj)) {
          return obj.map(cleanData).filter(item => item !== undefined);
        }
        if (typeof obj === 'object') {
          const cleaned = {};
          Object.keys(obj).forEach(key => {
            const cleanedValue = cleanData(obj[key]);
            if (cleanedValue !== undefined && cleanedValue !== null) {
              cleaned[key] = cleanedValue;
            }
          });
          return Object.keys(cleaned).length > 0 ? cleaned : undefined;
        }
        return obj;
      };

      // Prepare line data for Firestore
      const prepareLineData = (line) => {
        // Get the mockup URL using the consistent helper
        const mockupUrl = getMockupUrl(line);
        
        // Normalize files for Firestore (removes File objects, etc.)
        const normalizedFiles = normalizeFilesForFirestore(line.files);
        const dbType = convertTypeToDb(line.type) || 'screen';
        const qtySum = Object.values(line.sizes || {}).reduce((sum, q) => sum + (Number(q) || 0), 0);
        const price = Number(line.price || line.pricePerPiece || 0);
        const cogsVal = Number(line.cogs || 0);

        const base = {
          designName: line.design || line.designName || '',
          garmentId: line.style || line.garmentId || '',
          garmentColor: line.color || line.garmentColor || '',
          pricePerPiece: price,
          frontInkColors: line.frontInkColors || [],
          backInkColors: line.backInkColors || [],
          otherInkColors: line.otherInkColors || [],
          printLocations: line.printLocations || [],
          sizes: line.sizes || {},
          cogs: cogsVal,
          type: dbType,
          sizeType: line.sizeType || 'unisex',
          ...(normalizedFiles && { files: normalizedFiles }),
          ...(getPersistableMockupUrl(mockupUrl) && { designFileUrl: getPersistableMockupUrl(mockupUrl) }),
          totalQuantity: qtySum,
          totalLinePrice: qtySum * price,
          totalLineCOGS: qtySum * cogsVal,
          ...(line.inkNotes != null && { inkNotes: line.inkNotes }),
          ...(line.pressSetup && { pressSetup: line.pressSetup })
        };

        if (dbType === 'design_only') {
          return { ...base, ...designOnlyLineCompletionFields() };
        }

        return {
          ...base,
          status: line.status || LINE_STATUSES.NEW
        };
      };

      // Separate new lines from existing lines
      const newLines = lines.filter(line => line.isNew && !line.docId);
      const existingLines = lines.filter(line => line.modified && line.docId);

      // Create new lines
      const createPromises = newLines.map(async (line) => {
        // Upload production files with blob URLs before saving
        let updatedLine = { ...line };
        const poNumber = order.poNumber || order.orderNumber;
        
        if (line.files?.production && Array.isArray(line.files.production)) {
          const uploadedProductionFiles = await Promise.all(
            line.files.production.map(async (prodFile) => {
              // Skip if already has a real URL (not blob)
              if (prodFile.url && !prodFile.url.startsWith('blob:')) {
                return prodFile;
              }
              
              // Upload the file if we have the File object
              if (prodFile.file) {
                console.log('Uploading production file for new line:', prodFile.name);
                const sanitizedDesignName = (line.design || line.designName || 'unnamed').replace(/[^a-zA-Z0-9-_]/g, '_');
                const storageRef = ref(storage, `artwork/${poNumber}/${sanitizedDesignName}/Production/${prodFile.type || 'unassigned'}/${prodFile.file.name}`);
                await uploadBytes(storageRef, prodFile.file);
                const url = await getDownloadURL(storageRef);
                return {
                  name: prodFile.name,
                  url: url,
                  type: prodFile.type || 'unassigned'
                };
              }
              
              console.warn('⚠️ Production file has blob URL but no File object, skipping:', prodFile.name);
              return null;
            })
          );
          
          const validProductionFiles = uploadedProductionFiles.filter(f => f !== null);
          if (validProductionFiles.length > 0) {
            updatedLine.files = {
              ...updatedLine.files,
              production: validProductionFiles
            };
          }
        }
        
        const lineData = {
          ...prepareLineData(updatedLine),
          orderId: order.id,
          facilityId: resolveFacilityId(orderData.facilityId),
          lineNumber: line.lineNumber,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          activityLog: activityLogInit('Created', { details: 'Added via order edit' })
        };
        
        // Clean the data
        const cleanedData = cleanData(lineData);
        Object.keys(cleanedData).forEach(key => {
          if (cleanedData[key] === undefined || cleanedData[key] === null) {
            delete cleanedData[key];
          }
        });
        
        // Add to Firestore
        const docRef = await addDoc(collection(db, 'orderLines'), cleanedData);
        console.log('✅ Created new line:', docRef.id);
        return docRef;
      });

      // Update existing modified lines
      const updatePromises = existingLines.map(async (line) => {
        const lineRef = doc(db, 'orderLines', line.docId);
        
        const editedQty = Object.values(line.sizes || {}).reduce((s, q) => s + (Number(q) || 0), 0);
        const updateData = {
          ...prepareLineData(line),
          lineNumber: line.lineNumber,
          updatedAt: serverTimestamp()
        };
        
        // Clean the data
        const cleanedUpdateData = cleanData(updateData);
        Object.keys(cleanedUpdateData).forEach(key => {
          if (cleanedUpdateData[key] === undefined || cleanedUpdateData[key] === null) {
            delete cleanedUpdateData[key];
          }
        });

        // Append the activity entry AFTER cleaning: cleanData() recurses into objects
        // and would mangle the arrayUnion() field-value sentinel.
        Object.assign(cleanedUpdateData, activityLogAppend('Line edited', { details: `${editedQty} pcs total` }));
        
        console.log('✅ Updating existing line:', line.docId);
        return updateDoc(lineRef, cleanedUpdateData);
      });

      // Delete lines that were marked for deletion
      const deletePromises = deletedLineIds.map(async (lineDocId) => {
        const lineRef = doc(db, 'orderLines', lineDocId);
        console.log('🗑️ Deleting line:', lineDocId);
        return deleteDoc(lineRef);
      });

      // Execute all promises
      await Promise.all([...createPromises, ...updatePromises, ...deletePromises]);

      if (orderData.customerId) {
        const poNumber = order.poNumber || order.orderNumber;
        await Promise.all(
          lines.map((line) =>
            registerDesignMockupFromLine({
              customerId: orderData.customerId,
              line,
              orderId: order.id,
              orderNumber: poNumber
            })
          )
        );
      }

      await maybeSetOrderReadyToInvoiceIfAllLinesComplete(db, order.id);
      
      // Clear deleted line IDs after successful save
      setDeletedLineIds([]);
      
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error updating order:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(`Error updating order: ${error.message || 'Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMoveLine = useCallback((index, delta) => {
    setLines((prev) => moveLineInArray(prev, index, delta));
  }, []);

  const handleDuplicateLine = useCallback(
    (index) => {
      setLines((prev) => {
        const source = prev[index];
        if (!source) return prev;
        const duplicate = duplicateLineItem(source, {
          orderId: order?.id,
          lineNumber: prev.length + 1,
          isExpanded: true
        });
        return [...prev, duplicate];
      });
    },
    [order?.id]
  );

  // Handle adding a new line (only when explicitly requested)
  const handleAddLine = () => {
    const newLine = {
      id: generateUniqueId('line'),
      orderId: order.id,
      isExpanded: true,
      design: '',
      style: '',
      color: '',
      sizeType: 'unisex',
      sizes: {},
      frontInkColors: [],
      backInkColors: [],
      otherInkColors: [],
      printLocations: [],
      pricePerPiece: 0,
      price: 0,
      cogs: 0,
      type: LINE_TYPES.SCREEN_PRINT,
      status: 'new',
      lineNumber: lines.length + 1,
      isNew: true, // Mark as new line that needs to be created
      modified: true
    };
    setLines([...lines, newLine]);
  };

  const calculateTotalPieces = (lines) => {
    return lines.reduce((total, line) => {
      const linePieces = Object.values(line.sizes || {}).reduce((sum, qty) => sum + Number(qty || 0), 0);
      return total + linePieces;
    }, 0);
  };

  const calculatePricing = (lines, orderData, feeList = []) => {
    const linesCost = lines.reduce((total, line) => {
      const pieces = Object.values(line.sizes || {}).reduce((sum, qty) => sum + Number(qty || 0), 0);
      return total + (pieces * Number(line.pricePerPiece || 0));
    }, 0);

    const feesTotal = (Array.isArray(feeList) ? feeList : []).reduce((sum, fee) => {
      const quantity = Number(fee?.quantity || 0);
      const price = Number(fee?.price || 0);
      return sum + (quantity * price);
    }, 0);
    
    // Calculate 2XL+ upcharge
    const upchargePrice = (orderData.upchargePrice === '' || orderData.upchargePrice == null || Number.isNaN(parseFloat(orderData.upchargePrice))) ? 2 : parseFloat(orderData.upchargePrice);
    const upchargeQuantity = calculate2XLPlus(lines);
    const upchargeTotal = upchargeQuantity * upchargePrice;
    
    // Calculate subtotal (before discount)
    const subtotal = linesCost + feesTotal + upchargeTotal;
    
    // Calculate discount
    let discountAmount = 0;
    if (orderData.discount && orderData.discount.amount) {
      if (orderData.discount.type === 'percent') {
        discountAmount = subtotal * (parseFloat(orderData.discount.amount) / 100);
      } else {
        discountAmount = parseFloat(orderData.discount.amount) || 0;
      }
    }
    
    const orderTotal = subtotal - discountAmount;

    const merchSubtotal = linesCost + upchargeTotal;
    const taxableFeesTotal = sumTaxableFeesAmount(feeList);
    const taxExempt = resolveTaxExempt({
      order: orderData,
      customer: customerTaxExempt ? { taxExempt: true } : null,
    });
    const salesTax = computeSalesTax({
      subtotal: merchSubtotal,
      feesTotal,
      taxableFeesTotal,
      discountAmount,
      isTaxExempt: taxExempt,
    });

    const totalCogs = lines.reduce((total, line) => {
      const pieces = Object.values(line.sizes || {}).reduce((sum, qty) => sum + Number(qty || 0), 0);
      return total + (pieces * Number(line.cogs || 0));
    }, 0);

    const cogsPercentage = orderTotal > 0 ? (totalCogs / orderTotal) * 100 : 0;

    // Calculate print time and press rate
    const printMinutes = lines.reduce((total, line) => total + calculatePrintMinutes(line), 0);
    const printRevenue = lines.reduce((total, line) => total + calculatePrintRevenue(line), 0);
    const pressRate = calculatePressRate(printRevenue, printMinutes);

    return {
      subtotal,
      discountAmount,
      orderTotal,
      cogsPercentage,
      upchargeTotal,
      printMinutes,
      pressRate,
      feesTotal,
      salesTax
    };
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="1200px" closeOnOverlayClick={false}>
      <PageContainer>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <HeaderSection>
              <CustomerSearchContainer>
                <CustomerSearchInput
                  value={customerSearch}
                  onChange={handleCustomerSearch}
                  placeholder="SEARCH CUSTOMER..."
                />
                {filteredCustomers.length > 0 && (
                  <SearchResults>
                    {filteredCustomers.map(customer => (
                      <SearchResultItem
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        {customer.client}
                      </SearchResultItem>
                    ))}
                  </SearchResults>
                )}
              </CustomerSearchContainer>

              <ProjectManagerSelect
                value={orderData.projectManager || ''}
                onChange={(e) => setOrderData({...orderData, projectManager: e.target.value})}
              >
                <option value="">SELECT PM</option>
                {deduplicatePMs(projectManagers).map((pm, index) => (
                  <option key={`pm-${pm.name}-${index}`} value={pm.name}>
                    {pm.name}
                  </option>
                ))}
              </ProjectManagerSelect>

              <ProjectManagerSelect
                value={resolveFacilityId(orderData.facilityId)}
                onChange={(e) => setOrderData({ ...orderData, facilityId: e.target.value })}
                aria-label="Production Facility"
                title="Production Facility"
              >
                {FACILITIES.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </ProjectManagerSelect>

              <ProjectManagerSelect
                value={resolveFacilityId(orderData.homeFacilityId)}
                onChange={(e) => setOrderData({ ...orderData, homeFacilityId: e.target.value })}
                aria-label="Home Facility"
                title="Home Facility — customer emails, branding & pickup"
              >
                {FACILITIES.map((f) => (
                  <option key={f.id} value={f.id}>
                    Home: {f.label}
                  </option>
                ))}
              </ProjectManagerSelect>

              <DateContainer>
                <DateInput
                  type="date"
                  value={formatDateForInput(orderData.dueDate)}
                  onChange={(e) => setOrderData({...orderData, dueDate: e.target.value})}
                />
              </DateContainer>

              <UpdateButton 
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Updating...' : 'Update Order'}
              </UpdateButton>
            </HeaderSection>

            <LinesSection>
              {lines.map((line, index) => {
                // Map database values to LINE_TYPES values
                const mapPrintType = (dbType) => {
                  const typeMap = {
                    'screen': LINE_TYPES.SCREEN_PRINT,
                    'embroidery': LINE_TYPES.EMBROIDERY,
                    'dtg': LINE_TYPES.DTG,
                    'dtf': LINE_TYPES.DTF,
                    'design': LINE_TYPES.DESIGN_ONLY,
                    'design_only': LINE_TYPES.DESIGN_ONLY
                  };
                  return typeMap[dbType?.toLowerCase()] || LINE_TYPES.SCREEN_PRINT;
                };

                return (
                  <LineItem2
                    key={line.id}
                    lineData={{
                      ...line,
                      design: line.design || line.designName,        // Map to correct field names, prefer display name
                      style: line.style || line.garmentId,
                      color: line.color || line.garmentColor,
                      price: line.price || line.pricePerPiece,
                      type: mapPrintType(line.type)  // Map database value to LINE_TYPES value
                    }}
                    index={index}
                    orderNumber={order.poNumber || order.orderNumber}
                    lineId={line.docId || line.id}
                    customerId={orderData.customerId}
                    orderId={order.id}
                    onUpdate={(updatedLine) => handleLineUpdate(updatedLine, index)}
                    onDelete={() => handleDeleteRequest(line)}
                    onDuplicate={() => handleDuplicateLine(index)}
                    onMoveUp={() => handleMoveLine(index, -1)}
                    onMoveDown={() => handleMoveLine(index, 1)}
                    canMoveUp={index > 0}
                    canMoveDown={index < lines.length - 1}
                  />
                );
              })}
              <AddLineButton onClick={handleAddLine}>
                Add Line
              </AddLineButton>
            </LinesSection>

            <FooterSection>
              <NotesBox
                value={orderData.notes}
                onChange={(e) => setOrderData({...orderData, notes: e.target.value})}
                placeholder="Order Notes..."
              />

              <FeeGrid>
                {/* +2XL Upcharge Column */}
                <UpchargeColumn>
                  <FeeHeader>
                    <div style={{ padding: '4px', fontWeight: 600, fontSize: '0.85em' }}>+2XL Upcharge</div>
                  </FeeHeader>
                  <FeeRow>
                    <label>QTY:</label>
                    <input
                      type="text"
                      value={calculate2XLPlus(lines) || 0}
                      readOnly
                      style={{ backgroundColor: '#2D2D2D' }}
                    />
                  </FeeRow>
                  <FeeRow>
                    <label>$:</label>
                    <input
                      type="text"
                      value={orderData.upchargePrice === '' || orderData.upchargePrice == null ? '2.00' : orderData.upchargePrice}
                      onChange={(e) => setOrderData({...orderData, upchargePrice: e.target.value})}
                    />
                  </FeeRow>
                  <FeeRow>
                    <label>Total:</label>
                    <input
                      type="text"
                      value={((calculate2XLPlus(lines) || 0) * (parseFloat(orderData.upchargePrice) || 0)).toFixed(2)}
                      readOnly
                      style={{ backgroundColor: '#2D2D2D' }}
                    />
                  </FeeRow>
                </UpchargeColumn>

                {/* Fees, Discounts & Instructions Column */}
                <FeesColumn>
                  <div className="header">Fees, Discounts & Instructions</div>
                  <div className="summary">
                    {/* Fees */}
                    {fees.filter((fee) => fee.type && fee.quantity && fee.price).map((fee) => (
                      <div className="fee-line" key={fee.id}>
                        <span className="fee-type">{fee.type}</span>
                        <span className="fee-amount">${(Number(fee.quantity || 0) * Number(fee.price || 0)).toFixed(2)}</span>
                      </div>
                    ))}
                    {/* Discount */}
                    {orderData.discount && orderData.discount.amount && (
                      <div className="fee-line">
                        <span className="fee-type">Discount ({orderData.discount.type === 'percent' ? `${orderData.discount.amount}%` : 'Fixed'})</span>
                        <span className="fee-amount" style={{ color: '#FF6B6B' }}>
                          -${calculatePricing(lines, orderData, fees).discountAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {/* Instructions */}
                    {orderData.additionalInstructions && (
                      <div className="instructions">
                        {orderData.additionalInstructions.shipping && <span className="instruction-tag">Shipping</span>}
                        {orderData.additionalInstructions.sorting && <span className="instruction-tag">Sorting</span>}
                        {orderData.additionalInstructions.folding && <span className="instruction-tag">Folding</span>}
                        {orderData.additionalInstructions.bagging && <span className="instruction-tag">Bagging</span>}
                        {orderData.additionalInstructions.fulfillment && <span className="instruction-tag">Fulfillment</span>}
                        {orderData.additionalInstructions.neckTag && <span className="instruction-tag">Neck Tag</span>}
                        {orderData.additionalInstructions.taxExempt && <span className="instruction-tag" style={{ background: '#2196F3' }}>Tax Exempt (Order)</span>}
                        {customerTaxExempt && !orderData.additionalInstructions?.taxExempt && (
                          <span className="instruction-tag" style={{ background: '#2196F3' }}>Tax Exempt (Customer)</span>
                        )}
                        {orderData.additionalInstructions.designWorkOnly && <span className="instruction-tag" style={{ background: '#9C27B0' }}>Design Only</span>}
                        {orderData.additionalInstructions.doNotBill && <span className="instruction-tag" style={{ background: '#f44336' }}>Do Not Bill</span>}
                        {orderData.additionalInstructions.followUpEmail && <span className="instruction-tag" style={{ background: '#2196F3' }}>Follow-Up Email</span>}
                        {orderData.additionalInstructions.instagramWorthy && <span className="instruction-tag">Instagram Worthy</span>}
                        {orderData.additionalInstructions.providedGoods && <span className="instruction-tag" style={{ background: '#5B9BD5' }}>Provided Goods</span>}
                        {orderData.additionalInstructions.sample && <span className="instruction-tag" style={{ background: '#8E6BBE' }}>Sample</span>}
                        {orderData.additionalInstructions.other && <span className="instruction-tag" style={{ background: '#607D8B' }}>{orderData.additionalInstructions.other}</span>}
                      </div>
                    )}
                    {/* Empty state */}
                    {fees.filter((fee) => fee.type && fee.quantity && fee.price).length === 0 && !(orderData.discount && orderData.discount.amount) && !hasActiveInstructions(orderData.additionalInstructions) && (
                      <span style={{ fontStyle: 'italic' }}>No fees, discounts, or instructions</span>
                    )}
                  </div>
                  <EditFeesButton onClick={() => setShowFeesModal(true)}>
                    Edit
                  </EditFeesButton>
                </FeesColumn>

                {/* Totals Box */}
                <TotalsDisplay>
                  <div className="header">TOTALS</div>
                  <FeeRow>
                    <label>PCS:</label>
                    <input type="text" value={calculateTotalPieces(lines)} readOnly />
                  </FeeRow>
                  <FeeRow>
                    <label>$:</label>
                    <input
                      type="text"
                      value={(() => {
                        const pricing = calculatePricing(lines, orderData, fees);
                        const orderTotal = pricing.orderTotal;
                        const exempt = resolveTaxExempt({
                          order: orderData,
                          customer: customerTaxExempt ? { taxExempt: true } : null,
                        });
                        const tax = exempt ? 0 : pricing.salesTax;
                        return tax > 0 ? `$${orderTotal.toFixed(2)} + $${tax.toFixed(2)}/tax` : `$${orderTotal.toFixed(2)}`;
                      })()}
                      readOnly
                    />
                  </FeeRow>
                  <FeeRow>
                    <label>Stats:</label>
                    <input 
                      type="text" 
                      value={`${calculatePricing(lines, orderData, fees).cogsPercentage.toFixed(1)}% / ${formatMinutes(calculatePricing(lines, orderData, fees).printMinutes)} ${(() => {
                        const rate = calculatePricing(lines, orderData, fees).pressRate;
                        if (rate >= 350) return '🟢';
                        if (rate >= 300) return '🟡';
                        return '🔴';
                      })()}`}
                      readOnly 
                    />
                  </FeeRow>
                </TotalsDisplay>
              </FeeGrid>
            </FooterSection>

            {showSuccess && <SuccessAnimation onComplete={() => setShowSuccess(false)} />}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && lineToDelete && (
          <ConfirmDialog onClick={handleCancelDelete}>
            <ConfirmDialogContent onClick={e => e.stopPropagation()}>
              <ConfirmDialogTitle>Delete Line from Order?</ConfirmDialogTitle>
              <ConfirmDialogText>
                Are you sure you want to delete "{lineToDelete.design || lineToDelete.designName || 'this line'}"? 
                This action cannot be undone.
              </ConfirmDialogText>
              <ConfirmDialogButtons>
                <ConfirmButton onClick={handleCancelDelete}>
                  Cancel
                </ConfirmButton>
                <ConfirmButton variant="danger" onClick={handleConfirmDelete}>
                  Delete Line
                </ConfirmButton>
              </ConfirmDialogButtons>
            </ConfirmDialogContent>
          </ConfirmDialog>
        )}

        {/* Fees Modal */}
        <FeesModal
          isOpen={showFeesModal}
          onClose={() => setShowFeesModal(false)}
          shippingContext={{
            customerName: orderData.customerName || '',
            customerReference: orderData.customerId || '',
            orderReference: String(orderData.poNumber || orderData.orderNumber || order.id || '')
          }}
          initialData={{
            fees,
            discount: orderData.discount || { type: 'fixed', amount: '' },
            additionalInstructions: orderData.additionalInstructions || {},
            upchargePrice: orderData.upchargePrice,
            customerPoNumber: orderData.customerPoNumber || '',
            shippingDetails
          }}
          onSave={async (feesData) => {
            const sample = feesData.sample === true || feesData.additionalInstructions?.sample === true;
            const providedGoods = !sample && feesData.additionalInstructions?.providedGoods === true;
            const terminalOrderStatuses = [
              ORDER_STATUSES.READY_TO_INVOICE,
              ORDER_STATUSES.COMPLETED,
              ORDER_STATUSES.CANCELLED
            ];
            const canApplyProvidedGoodsStatus = !terminalOrderStatuses.includes(orderData.status);
            setFees(feesData.fees || []);
            setShippingDetails(feesData.shippingDetails || null);
            setOrderData(prev => ({
              ...prev,
              discount: feesData.discount,
              additionalInstructions: feesData.additionalInstructions,
              ...('upchargePrice' in feesData && { upchargePrice: feesData.upchargePrice }),
              ...('customerPoNumber' in feesData && { customerPoNumber: feesData.customerPoNumber }),
              ...(sample && { sample: true }),
              ...(providedGoods && canApplyProvidedGoodsStatus && { status: ORDER_STATUSES.ORDERED, providedGoods: true })
            }));

            if (sample && order?.id) {
              try {
                const linesQuery = query(
                  collection(db, 'orderLines'),
                  where('orderId', '==', order.id)
                );
                const linesSnapshot = await getDocs(linesQuery);
                const blockReason = await spPrintBlockReasonForCustomerLines(
                  db,
                  order.customerId,
                  linesSnapshot.docs.map((lineDoc) => lineDoc.data())
                );
                if (blockReason) {
                  alert(blockReason);
                  return;
                }
                const orderRef = doc(db, 'orders', order.id);
                await updateDoc(orderRef, {
                  sample: true,
                  additionalInstructions: feesData.additionalInstructions || null,
                  fees: feesData.fees || [],
                  ...(feesData.shippingDetails && { shippingDetails: feesData.shippingDetails }),
                  ...('upchargePrice' in feesData && { upchargePrice: feesData.upchargePrice }),
                  updatedAt: serverTimestamp()
                });
                await Promise.all(
                  linesSnapshot.docs.map((lineDoc) => {
                    const data = lineDoc.data();
                    if (isDesignOnlyLineType(data.type)) {
                      return updateDoc(doc(db, 'orderLines', lineDoc.id), designOnlyLineCompletionFields());
                    }
                    const patch = { ...sampleLineProductionSkipFields() };
                    if (data.status === LINE_STATUSES.COMPLETED && !data.isIn) {
                      patch.status = data.orderedAt ? LINE_STATUSES.ORDERED : LINE_STATUSES.NEW;
                    }
                    return updateDoc(doc(db, 'orderLines', lineDoc.id), patch);
                  })
                );
              } catch (err) {
                console.error('Error updating order/lines for Sample:', err);
                alert('Fees saved, but failed to apply Sample to lines. You may need to save the order again.');
              }
              return;
            }

            // If Provided Goods: skip Ready to Order — set order and all lines to ORDERED in Firestore
            if (providedGoods && canApplyProvidedGoodsStatus && order?.id) {
              try {
                const orderRef = doc(db, 'orders', order.id);
                await updateDoc(orderRef, {
                  status: ORDER_STATUSES.ORDERED,
                  additionalInstructions: feesData.additionalInstructions || null,
                  fees: feesData.fees || [],
                  ...(feesData.shippingDetails && { shippingDetails: feesData.shippingDetails }),
                  ...('upchargePrice' in feesData && { upchargePrice: feesData.upchargePrice }),
                  providedGoods: true,
                  updatedAt: serverTimestamp()
                });
                const linesQuery = query(
                  collection(db, 'orderLines'),
                  where('orderId', '==', order.id)
                );
                const linesSnapshot = await getDocs(linesQuery);
                await Promise.all(
                  linesSnapshot.docs.map((lineDoc) => {
                    const data = lineDoc.data();
                    const patch = isDesignOnlyLineType(data.type)
                      ? { ...designOnlyLineCompletionFields(), orderedAt: new Date() }
                      : { status: LINE_STATUSES.ORDERED, orderedAt: new Date() };
                    return updateDoc(doc(db, 'orderLines', lineDoc.id), patch);
                  })
                );
                await maybeSetOrderReadyToInvoiceIfAllLinesComplete(db, order.id);
              } catch (err) {
                console.error('Error updating order/lines for Provided Goods:', err);
                alert('Fees saved, but failed to set order to Provided Goods. You may need to save the order again.');
              }
            }
          }}
        />
      </PageContainer>
    </Modal>
  );
};

export default EditOrder; 